import { FC, useMemo } from "react";
import { MapPin, Truck } from "lucide-react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { JobStatus } from "~/utils/constants";
import { AssignmentTicket } from "./assignment-ticket";
import { Card, Badge } from "antd";
import { TServiceTicket } from "~/types";
import type { Dayjs } from "dayjs";

dayjs.extend(isoWeek);

type Props = {
  tickets: TServiceTicket[];
  onTicketClick?: (ticket: TServiceTicket) => void;
  targetServiceTicketId?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
  viewMode: "day" | "week";
};

// Utility function to get correct datetime by combining serviceDatetime date with startDatetime time
const getCorrectDateTime = (ticket: any) => {
  if (!ticket.serviceDatetime || !ticket.startDatetime) {
    return ticket.serviceDatetime || ticket.startDatetime;
  }

  const serviceDate = dayjs(ticket.serviceDatetime);
  const startTime = dayjs(ticket.startDatetime);

  // Combine: date from serviceDatetime + time from startDatetime
  return serviceDate
    .hour(startTime.hour())
    .minute(startTime.minute())
    .second(startTime.second())
    .millisecond(startTime.millisecond())
    .toDate();
};

const getStatusColor = (status: JobStatus) => {
  const statusMap: Record<string, string> = {
    [JobStatus.COMPLETED]: "success",
    [JobStatus.IN_PROGRESS]: "processing",
    [JobStatus.SCHEDULED]: "blue",
    [JobStatus.DRAFT]: "default",
    [JobStatus.CANCELLED]: "error",
  };
  return statusMap[status] || "default";
};

export const AssignmentsCalendarView: FC<Props> = ({ tickets, onTicketClick, targetServiceTicketId, startDate, endDate, viewMode }) => {
  // Group tickets by date
  const ticketsByDate = useMemo(() => {
    const grouped: Record<string, TServiceTicket[]> = {};

    tickets.forEach((ticket) => {
      const date = dayjs(getCorrectDateTime(ticket)).format("YYYY-MM-DD");
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(ticket);
    });

    // Sort tickets within each date by time
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => {
        const timeA = dayjs(getCorrectDateTime(a));
        const timeB = dayjs(getCorrectDateTime(b));
        return timeA.diff(timeB);
      });
    });

    return grouped;
  }, [tickets]);

  // Generate days to display based on view mode
  const daysToDisplay = useMemo(() => {
    const days = [];
    if (viewMode === "day") {
      // For day view, show just that day
      days.push(startDate);
    } else {
      // For week view, show the week (or extend to 2 weeks for better calendar view)
      const weekStart = startDate.startOf("isoWeek");
      const daysInRange = endDate.diff(weekStart, "day") + 1;
      
      // Show at least 2 weeks (14 days) for better calendar visualization
      const displayDays = Math.max(daysInRange, 14);
      for (let i = 0; i < displayDays; i++) {
        days.push(weekStart.add(i, "day"));
      }
    }
    return days;
  }, [startDate, endDate, viewMode]);

  const handleTicketClick = (ticket: TServiceTicket) => {
    if (onTicketClick) {
      onTicketClick(ticket);
    }
  };

  // Count tickets by status for each day
  const getDayTicketCounts = (dateKey: string) => {
    const dayTickets = ticketsByDate[dateKey] || [];
    return {
      scheduled: dayTickets.filter((t) => t.status === JobStatus.SCHEDULED).length,
      inProgress: dayTickets.filter((t) => t.status === JobStatus.IN_PROGRESS).length,
      completed: dayTickets.filter((t) => t.status === JobStatus.COMPLETED).length,
      total: dayTickets.length,
    };
  };

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile: Single column view */}
      <div className="block md:hidden space-y-3">
        {daysToDisplay.map((day) => {
          const dateKey = day.format("YYYY-MM-DD");
          const dayTickets = ticketsByDate[dateKey] || [];
          const isToday = day.isSame(dayjs(), "day");
          const isPast = day.isBefore(dayjs(), "day");
          const counts = getDayTicketCounts(dateKey);

          return (
            <Card
              key={dateKey}
              className={`border-2 ${
                isToday ? "border-blue-500 bg-blue-50" : isPast ? "border-gray-200 bg-gray-50" : "border-gray-300"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className={`text-xs font-bold uppercase tracking-wide ${isToday ? "text-blue-700" : "text-gray-700"}`}>
                    {day.format("ddd")}
                  </div>
                  <div className={`text-xl font-bold ${isToday ? "text-blue-700" : isPast ? "text-gray-400" : "text-gray-900"}`}>
                    {day.format("D")}
                  </div>
                </div>
                {counts.total > 0 && (
                  <div className="flex items-center gap-1">
                    <Badge count={counts.total} showZero={false} />
                  </div>
                )}
              </div>

              {dayTickets.length === 0 ? (
                <div className={`text-xs text-center py-4 ${isToday ? "text-blue-600" : "text-gray-400"}`}>
                  No assignments
                </div>
              ) : (
                <div className="space-y-2">
                  {dayTickets.map((ticket) => {
                    const ticketTime = dayjs(getCorrectDateTime(ticket));

                    return (
                      <div
                        key={ticket.id}
                        id={`ticket-${ticket.id}`}
                        className="border border-gray-300 p-2 rounded bg-white"
                        onClick={() => handleTicketClick(ticket)}
                      >
                        <AssignmentTicket
                          ticket={ticket}
                          variant="compact"
                          initialExpanded={ticket.id === targetServiceTicketId}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Desktop: Grid view */}
      <div className={`hidden md:grid gap-2 ${viewMode === "day" ? "md:grid-cols-1" : "md:grid-cols-7"}`}>
        {daysToDisplay.map((day) => {
          const dateKey = day.format("YYYY-MM-DD");
          const dayTickets = ticketsByDate[dateKey] || [];
          const isToday = day.isSame(dayjs(), "day");
          const isPast = day.isBefore(dayjs(), "day");
          const counts = getDayTicketCounts(dateKey);

          return (
            <div
              key={dateKey}
              className={`bg-white border-2 border-gray-300 p-2 min-h-[200px] rounded ${
                isToday ? "bg-blue-50 border-blue-500" : isPast ? "bg-gray-50" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className={`text-xs font-bold uppercase tracking-wide ${isToday ? "text-blue-700" : "text-gray-700"}`}>
                    {day.format("ddd")}
                  </div>
                  <div
                    className={`text-xl font-bold ${isToday ? "text-blue-700" : isPast ? "text-gray-400" : "text-gray-900"}`}
                  >
                    {day.format("D")}
                  </div>
                </div>
                {counts.total > 0 && (
                  <div className={`text-xs font-bold uppercase border-2 px-2 py-1 rounded ${
                    isToday ? "bg-blue-700 text-white border-blue-700" : "bg-gray-700 text-white border-gray-700"
                  }`}>
                    {counts.total}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {dayTickets.length === 0 ? (
                  <div className={`text-xs text-center py-4 ${isToday ? "text-blue-600" : "text-gray-400"}`}>
                    No assignments
                  </div>
                ) : (
                  dayTickets.map((ticket) => {
                    const ticketTime = dayjs(getCorrectDateTime(ticket));

                    return (
                      <div
                        key={ticket.id}
                        id={`ticket-${ticket.id}`}
                        className="border border-gray-300 p-2 cursor-pointer hover:bg-gray-100 bg-white rounded text-xs"
                        onClick={() => handleTicketClick(ticket)}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-bold truncate">
                              {ticket.jobTicket?.client?.companyName || ticket.client?.companyName || "Assignment"}
                            </span>
                            <Badge
                              status={getStatusColor(ticket.status) as any}
                              text={
                                <span className="text-xs">
                                  {ticket.status === JobStatus.SCHEDULED
                                    ? "Open"
                                    : ticket.status === JobStatus.IN_PROGRESS
                                    ? "In Progress"
                                    : "Completed"}
                                </span>
                              }
                            />
                          </div>

                          {ticket.vessel && (
                            <div className="flex items-center gap-1 text-xs">
                              <Truck size={10} />
                              <span className="truncate">{ticket.vessel.name || ticket.vessel.vesselRef}</span>
                            </div>
                          )}

                          <div className="text-xs font-bold">
                            {ticketTime.format("h:mm A")}
                          </div>

                          {ticket.location && (
                            <div className="flex items-center gap-1 text-xs">
                              <MapPin size={10} />
                              <span className="truncate">
                                {ticket.location.city || ticket.location.addressLine1 || ""}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

